var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import { useSelector } from 'react-redux';
import Button from '@doordash/component-button';
import Loading from '@doordash/component-loading';
import ListCell from '@doordash/component-list-cell';
import Banner from '@doordash/component-banner';
import { Colors, Icon, Inset, InlineChildren, Text, Spacing, withTheming, Stack, StackChildren, } from '@doordash/design-language';
import styled from 'styled-components';
import Separator from '@doordash/component-separator';
import { getSelectedTeamAccount, getSelectedTeamAccountContent, } from '../../../selectors/uiSelector';
import { useTeamAccountConfigurationContext } from '../../TeamAccountPlanDetails/TeamAccountConfigurationContext';
import { PaymentCardTestId } from './testId';
import { usePaymentCardQuery } from './usePaymentCardQuery';
import { usePaymentConfigQuery } from './usePaymentConfigQuery';
import PaymentCardCell from './PaymentCardCell';
import AddCardPayment from './AddCardPayment';
import InfoBanner, { BannerType } from '../../Common/InfoBanner';
import PaymentStrings from './PaymentStrings';
import track from '../../../utils/track';
import { OnboardingStep } from '../../../types/GloblalTypes';
import { useShouldOperationSetOnboardingStep, useSetOnboardingStepMutation, } from '../../SelfServe/Onboarding/useSetOnboardingStepMutation';
import { EXPENSED_MEALS_STATUS } from '../../../types/expensed_meal';
import { DASHPASS_STATUS } from '../../../types/dashpass';
import { useIsDynamicBooleanEnabled } from '../../DynamicValues/hook';
import { DYNAMIC_VALUES_NAME } from '../../DynamicValues';
import { CONTACT_SUPPORT_URL } from '../../../constants/values';
import { BillingPaymentCardEvents } from '../../../constants/events';
export var PaymentCardContainer = withTheming(styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  background-color: ", ";\n  border: 1px solid ", ";\n  width: 100%;\n  margin: ", "px 0;\n"], ["\n  background-color: ", ";\n  border: 1px solid ", ";\n  width: 100%;\n  margin: ", "px 0;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.Colors.PageBackgroundPrimary;
}, function (_a) {
    var theme = _a.theme;
    return theme.Colors.BorderSecondary;
}, Spacing.Small));
var PaymentSectionTitle = styled(Text).attrs({
    styles: Text.Styles.Medium14,
})(templateObject_2 || (templateObject_2 = __makeTemplateObject([""], [""])));
var PaymentMethodText = styled(PaymentSectionTitle)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  padding-bottom: ", "px;\n"], ["\n  padding-bottom: ", "px;\n"])), Spacing.XSmall);
var SectionTitleText = styled(PaymentSectionTitle)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  padding: ", "px;\n"], ["\n  padding: ", "px;\n"])), Spacing.Small);
var CenterContainer = styled.div(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  flex-wrap: wrap;\n  justify-content: center;\n  align-items: center;\n  padding: ", "px;\n  margin: auto ", "px;\n"], ["\n  display: flex;\n  flex-direction: column;\n  flex-wrap: wrap;\n  justify-content: center;\n  align-items: center;\n  padding: ", "px;\n  margin: auto ", "px;\n"])), Spacing.Large, Spacing.XSmall);
var ErrorContainer = styled(CenterContainer)(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  background-color: ", ";\n"], ["\n  background-color: ", ";\n"])), Colors.SystemRed0);
var getTeamAccountBenefitTypes = function (teamAcc) {
    var _a, _b;
    var isEmActivated = ((_a = teamAcc === null || teamAcc === void 0 ? void 0 : teamAcc.expensedMeals) === null || _a === void 0 ? void 0 : _a.status) === EXPENSED_MEALS_STATUS.ACTIVE;
    var isDpActivated = ((_b = teamAcc === null || teamAcc === void 0 ? void 0 : teamAcc.dashPass) === null || _b === void 0 ? void 0 : _b.status) ===
        DASHPASS_STATUS.TEAM_ACCOUNT_DASHPASS_SUBSCRIPTION_STATUS_ACTIVE;
    return {
        emActivated: isEmActivated,
        dpActivated: isDpActivated,
    };
};
var getNoCreditCardWarningText = function (teamAcc, isSelfServeDpEnabled) {
    if (!isSelfServeDpEnabled) {
        return PaymentStrings.noCreditCardWarningTextForEmOnlyAccount();
    }
    var teamAccBenefitTypes = getTeamAccountBenefitTypes(teamAcc);
    if ((teamAccBenefitTypes.dpActivated && !teamAccBenefitTypes.emActivated) ||
        (!teamAccBenefitTypes.dpActivated && !teamAccBenefitTypes.emActivated) // this indicates a DP-first flow, DP benefit is not yet activated
    ) {
        return PaymentStrings.noCreditCardWarningTextForDpOnlyAccount();
    }
    if (teamAccBenefitTypes.emActivated && teamAccBenefitTypes.dpActivated) {
        return PaymentStrings.noCreditCardWarningTextForEmAndDpAccount();
    }
    return PaymentStrings.noCreditCardWarningTextForEmOnlyAccount();
};
var ErrorState = function (_a) {
    var refetch = _a.refetch;
    return (React.createElement(ErrorContainer, null,
        React.createElement(Stack, null,
            React.createElement(InlineChildren, null,
                React.createElement(Icon, { type: Icon.Types.ErrorLine }),
                React.createElement(Text, null, PaymentStrings.errorFetchingCards()))),
        React.createElement(Stack, { size: Stack.Sizes.XxxSmall },
            React.createElement(Button, { type: Button.Types.Primary, accessibilityLabel: "retry", onClick: function () { return refetch(); } }, PaymentStrings.tryAgain()))));
};
var PaymentCard = function () {
    var _a;
    var selectedTeamAccountId = useSelector(getSelectedTeamAccount);
    var selectedTeamAccount = useSelector(getSelectedTeamAccountContent);
    var isSelfServeDpEnabled = useIsDynamicBooleanEnabled(DYNAMIC_VALUES_NAME.ddfwSelfServeDP);
    var _b = __read(React.useState(false), 2), displayCardAddSuccessBanner = _b[0], setDisplayCardAddSuccessBanner = _b[1];
    var _c = __read(React.useState(false), 2), showAddCardModal = _c[0], setShowAddCardModal = _c[1];
    var _d = useTeamAccountConfigurationContext(), maxAddPaymentMethodLimit = _d.maxAddPaymentMethodLimit, reloadTeamAccountPlanDetails = _d.reloadTeamAccountPlanDetails;
    var _e = usePaymentCardQuery(selectedTeamAccountId), data = _e.data, loading = _e.loading, error = _e.error, refetch = _e.refetch;
    var paymentConfig = usePaymentConfigQuery(selectedTeamAccountId).data;
    var paymentCards = (_a = data === null || data === void 0 ? void 0 : data.getPaymentMethodListByTeamAccount) !== null && _a !== void 0 ? _a : [];
    var setOnboardingStep = useSetOnboardingStepMutation(selectedTeamAccountId, OnboardingStep.ONBOARDING_STEP_ADD_PAYMENT_METHOD).setOnboardingStep;
    var hasCompletedBudgetOnboardingStep = !useShouldOperationSetOnboardingStep(selectedTeamAccountId, OnboardingStep.ONBOARDING_STEP_ADD_BUDGET);
    var hasCompletedPaymentOnboardingStep = !useShouldOperationSetOnboardingStep(selectedTeamAccountId, OnboardingStep.ONBOARDING_STEP_ADD_PAYMENT_METHOD);
    // for FTUX onboarding experience when Cx adds budget before adding payment method
    var shouldShowPaymentAddWarningBanner = !loading &&
        hasCompletedBudgetOnboardingStep &&
        !hasCompletedPaymentOnboardingStep &&
        paymentCards.length === 0;
    var onAddCardComplete = function () {
        setDisplayCardAddSuccessBanner(true);
        setOnboardingStep();
        // since payment add count is not something we can keep track of like budgets/members, we refetch it from TMS
        reloadTeamAccountPlanDetails === null || reloadTeamAccountPlanDetails === void 0 ? void 0 : reloadTeamAccountPlanDetails();
    };
    return (React.createElement(React.Fragment, null,
        !loading &&
            paymentCards.length === 0 &&
            !shouldShowPaymentAddWarningBanner && (React.createElement(InfoBanner, { bannerText: getNoCreditCardWarningText(selectedTeamAccount, isSelfServeDpEnabled), bannerType: BannerType.WARNING })),
        displayCardAddSuccessBanner && (React.createElement(InfoBanner, { bannerTitle: PaymentStrings.paymentAddSuccessInfoBannerTitle(), bannerText: PaymentStrings.paymentAddSuccessInfoBannerDescription(), bannerType: BannerType.INFORMATIONAL, onClose: function () { return setDisplayCardAddSuccessBanner(false); } })),
        shouldShowPaymentAddWarningBanner && (React.createElement(Banner, { label: "", body: PaymentStrings.paymentAddWarningBannerText(), primaryAction: {
                content: PaymentStrings.paymentAddWarningBannerPrimaryButton(),
                onClick: function () { return setShowAddCardModal(true); },
            }, variant: Banner.Variants.Warning, type: Banner.Types.Default })),
        React.createElement(PaymentCardContainer, { "data-testid": PaymentCardTestId.Container },
            React.createElement(Stack, null,
                React.createElement(StackChildren, { size: StackChildren.Sizes.Large, "data-testid": PaymentCardTestId.Header },
                    React.createElement(ListCell, { isToggleable: false, title: React.createElement(PaymentMethodText, null, PaymentStrings.currentPaymentMethod()), subtext: React.createElement(Text, { styles: Text.Styles.Bold22 }, PaymentStrings.currentPaymentMethodSubText()) })),
                React.createElement(StackChildren, { size: StackChildren.Sizes.Large, "data-testid": PaymentCardTestId.SavedCards },
                    React.createElement(Inset, { size: Inset.Sizes.XxxSmall },
                        React.createElement(SectionTitleText, null, PaymentStrings.savedCreditCardTitle()),
                        loading && (React.createElement("div", { "data-testid": "Loading" },
                            React.createElement(Loading, null))),
                        error && React.createElement(ErrorState, { refetch: refetch }),
                        paymentCards.map(function (card, index) {
                            var id = card.id;
                            return (React.createElement(PaymentCardCell, { key: id, card: card, teamAccountId: selectedTeamAccountId, isSeparatorVisible: index !== paymentCards.length - 1 }));
                        }))),
                React.createElement(Separator, { type: Separator.Types.InsetBorder }),
                React.createElement(Inset, { size: Inset.Sizes.XxxSmall },
                    React.createElement(SectionTitleText, null, PaymentStrings.addNewCardSectionText())),
                React.createElement(AddCardPayment, { teamAccountId: selectedTeamAccountId, paymentConfig: paymentConfig === null || paymentConfig === void 0 ? void 0 : paymentConfig.getPaymentConfigForTeamAccount, isPaymentMethodAddLimitReached: maxAddPaymentMethodLimit === null || maxAddPaymentMethodLimit === void 0 ? void 0 : maxAddPaymentMethodLimit.isLimitReached, maxPaymentMethodAddLimit: maxAddPaymentMethodLimit === null || maxAddPaymentMethodLimit === void 0 ? void 0 : maxAddPaymentMethodLimit.limit, setShowAddCardModal: function (val) { return setShowAddCardModal(val); }, showAddCardModal: showAddCardModal, onAddCardComplete: function () { return onAddCardComplete(); } }))),
        React.createElement(Text, { "data-testid": PaymentCardTestId.ContactSupport },
            "If you have questions or would like to get help,",
            ' ',
            React.createElement("a", { href: CONTACT_SUPPORT_URL, target: "_blank", rel: "noopener noreferrer", onClick: function () { return track(BillingPaymentCardEvents.SUPPORT_LINK_CLICK); } }, "contact support."))));
};
export default PaymentCard;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6;
